import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Link } from "react-router-dom";
import {
  Users, Calendar, Briefcase, Plus, Activity, CheckCircle2, AlertTriangle,
  Layers,
} from "lucide-react";
import {
  PageHeader, KPIGrid, KPICard, FilterBar, EmptyState, AvatarStack,
  StatusBadge, PrimaryButton,
} from "@/components/app/PageKit";
import { useAuth } from "@/lib/auth";

export default function Projects() {
  const { user } = useAuth();
  const [projs, setProjs] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  useEffect(() => {
    api.get("/projects").then(r => setProjs(r.data || [])).catch(() => {});
    api.get("/employees").then(r => setEmployees(r.data || [])).catch(() => {});
  }, []);

  const empMap = useMemo(() => Object.fromEntries(employees.map(e => [e.id, e])), [employees]);

  const filtered = useMemo(() => projs.filter(p => {
    if (search && !p.name?.toLowerCase().includes(search.toLowerCase())) return false;
    if (statusFilter && p.status !== statusFilter) return false;
    return true;
  }), [projs, search, statusFilter]);

  const kpi = {
    total: projs.length,
    active: projs.filter(p => p.status === "active" || p.status === "on_track").length,
    at_risk: projs.filter(p => p.status === "at_risk").length,
    avg_progress: projs.length ? Math.round(projs.reduce((a, p) => a + (p.progress || 0), 0) / projs.length) : 0,
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5 max-w-full overflow-x-hidden" data-testid="projects-page">
      <PageHeader
        category="Work Management"
        title="Projects"
        description={`${projs.length} ${projs.length === 1 ? "project" : "projects"} across the organization`}
        primaryAction={["super_admin", "hr_admin", "manager", "department_head"].includes(user?.role) ? {
          label: "New Project",
          icon: Plus,
          onClick: () => alert("Create project flow — backend pending"),
          testid: "new-project-btn",
        } : undefined}
      />

      <KPIGrid cols={4}>
        <KPICard icon={Briefcase}    tone="violet"  label="Total Projects" value={kpi.total} testid="kpi-total" />
        <KPICard icon={Activity}     tone="emerald" label="Active"          value={kpi.active} testid="kpi-active" />
        <KPICard icon={AlertTriangle}tone="amber"   label="At Risk"         value={kpi.at_risk} testid="kpi-risk" />
        <KPICard icon={CheckCircle2} tone="blue"    label="Avg Progress"    value={`${kpi.avg_progress}%`} testid="kpi-progress" />
      </KPIGrid>

      <FilterBar
        search={search}
        onSearch={setSearch}
        searchPlaceholder="Search projects…"
        filters={[{
          label: "All status",
          value: statusFilter,
          onChange: setStatusFilter,
          options: ["active", "on_track", "at_risk", "delayed", "completed"],
          testid: "filter-status",
        }]}
      />

      {filtered.length === 0 ? (
        <EmptyState
          icon={Layers}
          title={projs.length === 0 ? "No projects yet" : "No projects match your filters"}
          message={projs.length === 0 ? "Spin up your first project and assign owners, milestones and members." : "Try clearing search or status filters."}
          action={projs.length === 0 ? { label: "Create Project", icon: Plus, onClick: () => {} } : undefined}
        />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((p) => {
            const members = (p.members || []).map(id => empMap[id]).filter(Boolean);
            return (
              <Link key={p.id} to={`/projects/${p.id}`} data-testid={`project-card-${p.id}`}>
                <Card className="p-5 border-slate-200 shadow-sm bg-white hover:shadow-md hover:border-violet-200 transition h-full">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="min-w-0">
                      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-1">{p.code || "PROJECT"}</div>
                      <h3 className="font-[Outfit] font-bold text-slate-900 text-base leading-tight truncate">{p.name}</h3>
                    </div>
                    <StatusBadge status={p.status || "active"} />
                  </div>
                  {p.description && <p className="text-[12px] text-slate-600 line-clamp-2">{p.description}</p>}

                  <div className="mt-4">
                    <div className="flex items-center justify-between text-[11px] text-slate-500 mb-1">
                      <span className="font-semibold uppercase tracking-wider">Progress</span>
                      <span className="font-bold text-slate-900 tabular-nums">{p.progress || 0}%</span>
                    </div>
                    <Progress value={p.progress || 0} className="h-1.5" />
                  </div>

                  <div className="mt-4 flex items-center justify-between">
                    {members.length > 0 ? <AvatarStack people={members} max={4} /> : (
                      <span className="text-[11px] text-slate-400 inline-flex items-center gap-1"><Users className="w-3 h-3" /> No members</span>
                    )}
                    {p.end_date && (
                      <span className="text-[11px] text-slate-500 inline-flex items-center gap-1">
                        <Calendar className="w-3 h-3" /> {new Date(p.end_date).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                      </span>
                    )}
                  </div>
                </Card>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
